/**
 * Talk-tab feed model. `MessageStore` is a sync, deduped, capped feed buffer per
 * band. `decodeMessage` turns a firehose ReceivedEvent into a MessageEntry —
 * banding it via talk-geo and unsealing its text (a thrown openBroadcast → a
 * `text: null` locked placeholder, so the *presence* of a message is still shown).
 */
import type { ReceivedEvent } from "@beacon/client-core";
import { bandFor, type Band } from "./talk-geo.js";

interface SealedBroadcastRef { ciphertext: { $bytes: string }; keyRef: string }

export interface MessageEntry {
  id: string;
  author: string;
  createdAtIso: string;
  band: Band;
  km?: number;
  /** decrypted text, or null when the keyRef is unknown/shredded (locked placeholder). */
  text: string | null;
  lang?: string;
}

export class MessageStore {
  static readonly CAP = 200; // per band (ring buffer)
  #byId = new Map<string, MessageEntry>();
  #order = new Map<string, number>();
  #seq = 0;

  /** Add an entry; returns false (no-op) if its id was already seen. Evicts the
   *  oldest entry in the same band once the band exceeds CAP. */
  add(e: MessageEntry): boolean {
    if (this.#byId.has(e.id)) return false;
    this.#byId.set(e.id, e);
    this.#order.set(e.id, this.#seq++);
    const sameBand = [...this.#byId.values()].filter((x) => x.band === e.band);
    if (sameBand.length > MessageStore.CAP) {
      let oldest = sameBand[0];
      for (const x of sameBand) if (this.#order.get(x.id)! < this.#order.get(oldest.id)!) oldest = x;
      this.#byId.delete(oldest.id);
      this.#order.delete(oldest.id);
    }
    return true;
  }

  /** Messages in `band`, oldest-first (createdAt, then insertion order as tiebreak). */
  list(band: Band): MessageEntry[] {
    return this.#sorted((x) => x.band === band);
  }

  /** Messages to show for a Talk scope, oldest-first. "area" shows only the
   *  area band; "region" is a SUPERSET — it shows everything within the region
   *  (area + region bands), so your own messages (always banded "area", since
   *  home ∈ area set) and nearby messages appear alongside wider-region ones.
   *  "distant" is never shown. */
  listScope(scope: "area" | "region"): MessageEntry[] {
    if (scope === "area") return this.#sorted((x) => x.band === "area");
    return this.#sorted((x) => x.band === "area" || x.band === "region");
  }

  /** Entries matching `keep`, oldest-first (createdAt, then insertion order as tiebreak). */
  #sorted(keep: (e: MessageEntry) => boolean): MessageEntry[] {
    return [...this.#byId.values()]
      .filter(keep)
      .sort((a, b) =>
        a.createdAtIso < b.createdAtIso ? -1 :
        a.createdAtIso > b.createdAtIso ? 1 :
        this.#order.get(a.id)! - this.#order.get(b.id)!,
      );
  }
}

export interface DecodeCtx { areaSet: Set<string>; regionSet: Set<string>; homeCell: string }

/** Decode a firehose event into a MessageEntry, or null if it is not a message. */
export async function decodeMessage(
  evt: ReceivedEvent,
  ctx: DecodeCtx,
  open: (sealed: SealedBroadcastRef) => Promise<Uint8Array>,
): Promise<MessageEntry | null> {
  const json = evt.json ?? {};
  if (json["$type"] !== "app.aid.message") return null;
  const p = (json.payload ?? {}) as Record<string, unknown>;
  const geo = (json.geo ?? {}) as { cell?: string };
  const { band, km } = bandFor(geo.cell ?? "", ctx.areaSet, ctx.regionSet, ctx.homeCell);
  let text: string | null;
  try {
    text = new TextDecoder().decode(await open(p.text as SealedBroadcastRef));
  } catch {
    text = null; // unknown/shredded keyRef → locked placeholder (never dropped)
  }
  return {
    id: json.id as string,
    author: json.identityId as string,
    createdAtIso: (json.createdAt as string) ?? "",
    band,
    km,
    text,
    lang: p.lang as string | undefined,
  };
}
